import { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to poll for incoming calls
 * @param {boolean} enabled - Whether to poll (should be true when user is logged in)
 * @param {string} authToken - The authentication token
 * @param {number} pollInterval - Polling interval in ms (default: 3000)
 */
function useIncomingCall(enabled, authToken, pollInterval = 3000) {
  const [incomingCall, setIncomingCall] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const intervalRef = useRef(null);

  const checkForIncomingCall = useCallback(async () => {
    if (!authToken || isChecking) return;
    
    setIsChecking(true);
    try {
      const response = await fetch(`${API_URL}/api/video/call/check`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.has_incoming_call && data.call) {
          setIncomingCall(data.call);
        } else {
          setIncomingCall(null);
        }
      }
    } catch (error) {
      console.error('[IncomingCall] Error checking for calls:', error);
    } finally {
      setIsChecking(false);
    }
  }, [authToken, isChecking]);

  const acceptCall = useCallback(async () => {
    if (!incomingCall || !authToken) return;
    
    try {
      await fetch(`${API_URL}/api/video/call/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          appointment_id: incomingCall.appointment_id,
          response: 'accepted'
        })
      });
      
      const call = incomingCall;
      setIncomingCall(null);
      return call;
    } catch (error) {
      console.error('[IncomingCall] Error accepting call:', error);
      return null;
    }
  }, [incomingCall, authToken]);

  const rejectCall = useCallback(async () => {
    if (!incomingCall || !authToken) return;
    
    try {
      await fetch(`${API_URL}/api/video/call/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          appointment_id: incomingCall.appointment_id,
          response: 'rejected'
        })
      });
    } catch (error) {
      console.error('[IncomingCall] Error rejecting call:', error);
    } finally {
      setIncomingCall(null);
    }
  }, [incomingCall, authToken]);

  // Start/stop polling based on enabled state
  useEffect(() => {
    if (enabled && authToken) {
      // Check immediately
      checkForIncomingCall();
      
      // Then poll periodically
      intervalRef.current = setInterval(checkForIncomingCall, pollInterval);
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setIncomingCall(null);
    }
  }, [enabled, authToken, pollInterval, checkForIncomingCall]);

  return {
    incomingCall,
    acceptCall,
    rejectCall,
    clearCall: () => setIncomingCall(null)
  };
}

export default useIncomingCall;
